/** @jsxImportSource @emotion/react */
import { Form } from 'react-final-form'

import { FormApi } from 'final-form'

import { InputField } from 'components/Input'
import { EditorField } from 'components/Editor'
import { validateRequiredHtml } from 'utils/validation'
import { EmailTemplateTypeEnum } from '__generatedTypes__/globalTypes'
import { stripHTML } from 'utils/stripHTML'
import { toast } from 'utils/toast'
import { FooterButtons } from '../FooterButtons'
import { CheckboxWithTitle } from '../CheckboxWithTitle'
import * as commonClasses from '../commonStyles'
import { useEmailTemplate } from '../hooks'
import { validateEmailSubject } from '../consts'

const testData = {
  username: 'User',
  user_email: 'user@example.com',
  subscription_name: 'Premium Max',
  amount: 100,
  discount_code: 'BLACK_FRIDAY',
  invoice_url: 'https://www.google.com',
}

type FormValues = {
  subject: string
  content: string
  disableForAdmin: boolean
}

type Props = {
  type: EmailTemplateTypeEnum
  platformId?: string
}

export const AdminForm = ({ type, platformId }: Props) => {
  const { template, updateTemplate, updateTemplateLoading, sendEmail, sendEmailLoading } = useEmailTemplate(
    type,
    platformId
  )

  const initialValues: FormValues = {
    subject: template?.subject || '',
    content: template?.emailAsHtml || '',
    disableForAdmin: template?.config?.isDisabledForAdmins || false,
  }

  const handleSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
    try {
      await updateTemplate({
        id: template?.id,
        subject: values.subject,
        emailAsHtml: values.content,
        emailAsPlainText: stripHTML(values.content),
        config: {
          isDisabledForAdmins: values.disableForAdmin,
        },
      })
      form.reset(values)

      toast.success('Admin email template updated successfully')
    } catch (error) {
      toast.error('Failed to update admin email template')
    }
  }

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, dirty }) => (
        <form onSubmit={handleSubmit} css={commonClasses.form}>
          <InputField name="subject" label="Subject" validate={validateEmailSubject} css={commonClasses.input} />

          <EditorField
            type="default"
            name="content"
            label="Content"
            placeholder="Enter email content"
            validate={validateRequiredHtml}
          />

          <CheckboxWithTitle
            name="disableForAdmin"
            title="Disable for Admin"
            label="Check this to disable the email sent out to the administrator when a new member becomes active."
          />

          <FooterButtons
            onPreviewClick={sendEmail(testData)}
            previewDisabled={updateTemplateLoading || sendEmailLoading}
            submitDisabled={updateTemplateLoading || sendEmailLoading || !dirty}
          />
        </form>
      )}
    />
  )
}
